import React from 'react';
import { BalanceReturn, TenureReturn } from '../clients/rust-api-client';
import { Badge } from '../common/Catalyst/badge';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import {
  agingSumsToBalance,
  formatBillingDate,
  formatMoney,
  isNegativeMoney,
} from '../utils/billingHelpers';

interface Props {
  balance: BalanceReturn;
  /** The home's active tenure, for the HOLD override. */
  tenure?: TenureReturn | null;
}

const Figure: React.FC<{
  label: string;
  value: React.ReactNode;
  help?: React.ReactNode;
  emphasis?: boolean;
}> = ({ label, value, help, emphasis }) => (
  // The testid is how a test reads one figure rather than the first
  // matching amount on the card — several of these legitimately show
  // the same number.
  <div data-testid={`figure-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-sm text-zinc-500 dark:text-zinc-400">{label}</div>
    <div
      className={
        emphasis
          ? 'mt-1 text-2xl font-semibold text-zinc-900 dark:text-white'
          : 'mt-1 text-lg text-zinc-900 dark:text-white'
      }
    >
      {value}
    </div>
    {help && (
      <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
        {help}
      </div>
    )}
  </div>
);

/**
 * What this home owes, and how old it is.
 *
 * Three things this card has to get right:
 *
 * **A credit balance is not a debt.** When the HOA allows credit
 * balances the figure can go below zero, and it means the association
 * holds the homeowner's money.
 *
 * **`current_period_amount` can be `null`, and that is not zero.** It
 * means no dues schedule is in force — the association bills nothing
 * recurring, which a manager needs to see as "unconfigured" rather than
 * as "up to date".
 *
 * **The aging buckets sum to the balance**, so the card checks it. The
 * identity does not hold for a credit balance (no open charges to
 * bucket), which `agingSumsToBalance` reports separately rather than as
 * a mismatch.
 */
const HomeBalanceCard: React.FC<Props> = ({ balance, tenure }) => {
  const credit = isNegativeMoney(balance.balance);
  const check = agingSumsToBalance(balance.aging, balance.balance);
  const period = balance.current_period_amount;

  const buckets: { label: string; value: string }[] = [
    { label: 'Not yet due', value: balance.aging.current },
    { label: '1–30 days', value: balance.aging.days_1_30 },
    { label: '31–60 days', value: balance.aging.days_31_60 },
    { label: '61–90 days', value: balance.aging.days_61_90 },
    { label: 'Over 90 days', value: balance.aging.days_over_90 },
  ];

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-6">
      <div className="grid gap-6 sm:grid-cols-3">
        <Figure
          label={credit ? 'Held on account' : 'Outstanding balance'}
          emphasis
          value={
            <span
              className={
                credit ? 'text-lime-700 dark:text-lime-400' : undefined
              }
            >
              {formatMoney(balance.balance)}
            </span>
          }
          help={
            credit
              ? 'The association holds this on the homeowner’s behalf.'
              : undefined
          }
        />
        <Figure
          label="This period"
          value={
            period === null || period === undefined ? (
              <span className="text-zinc-500 dark:text-zinc-400">
                No schedule
              </span>
            ) : (
              formatMoney(period)
            )
          }
          help={
            period === null || period === undefined
              ? 'No dues schedule is in force, so nothing recurring is billed. That is not the same as billing zero.'
              : 'Every schedule in force, added together.'
          }
        />
        <Figure
          label="Oldest unpaid"
          value={
            balance.oldest_unpaid_due_date
              ? formatBillingDate(balance.oldest_unpaid_due_date)
              : '—'
          }
          help={
            balance.days_past_due > 0
              ? `${balance.days_past_due} days past due`
              : 'Nothing past due.'
          }
        />
      </div>

      {tenure && (
        <div className="mt-6 flex items-center gap-3">
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            Payer file HOLD
          </span>
          {tenure.hold_override === true && (
            <Badge color="red">Forced on</Badge>
          )}
          {tenure.hold_override === false && (
            <Badge color="sky">Forced off</Badge>
          )}
          {(tenure.hold_override === null ||
            tenure.hold_override === undefined) && (
            <Badge color="zinc">Following collections policy</Badge>
          )}
          <Text className="text-xs">
            {tenure.hold_override === null || tenure.hold_override === undefined
              ? 'Whether the file actually holds depends on this home’s collections case — the delinquency report shows the result.'
              : 'Set by hand, so the collections policy is not consulted.'}
          </Text>
        </div>
      )}

      <div className="mt-6">
        <Subheading level={4}>Aging</Subheading>
        <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-5">
          {buckets.map((b) => (
            <div key={b.label}>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">
                {b.label}
              </div>
              <div className="mt-1 text-sm font-medium text-zinc-900 dark:text-white">
                {formatMoney(b.value)}
              </div>
            </div>
          ))}
        </div>

        {check.status === 'mismatch' && (
          <Text className="mt-3 text-amber-700 dark:text-amber-400">
            These buckets add up to {formatMoney(check.bucketTotal)}, but the
            balance is {formatMoney(check.balance)}. They are meant to be the
            same number — worth reporting rather than working around.
          </Text>
        )}
        {check.status === 'not_applicable' && credit && (
          <Text className="mt-3">
            A credit balance has no open charges to age, so the buckets are
            empty rather than adding up to it.
          </Text>
        )}
      </div>
    </div>
  );
};

export default HomeBalanceCard;
